import { useCallback } from 'react';
import {
  type ConfigEntity,
  type EnvironmentKey,
  type ConfigEntityRootData,
  EntityStatusValue,
} from '@web-config-app/core';
import {
  useEntityUpdate,
  UseEntityUpdateOptions,
} from '../use-entity-update/use-entity-update';
import { alterDataForEnvironmentEntitySync } from '../../utilities/alter-data-for-environment-sync/alter-data-for-environment-sync';

interface UseEntityEnvironmentSyncProps {
  instanceId: string;
  configEntity: ConfigEntity;
  otherEnvironmentKey: EnvironmentKey;
  currentEnvironmentData?: ConfigEntityRootData;
  otherEnvironmentData?: ConfigEntityRootData;
  options?: UseEntityUpdateOptions;
}

export const useEntityEnvironmentSync = ({
  configEntity,
  instanceId,
  otherEnvironmentKey,
  currentEnvironmentData,
  otherEnvironmentData,
  options,
}: UseEntityEnvironmentSyncProps) => {
  // Decide operation: if target environment entity is published then publish, otherwise it is in draft so we update.
  const targetStatus = otherEnvironmentData?.attributes?.entityMetadata?.status;
  const operation =
    targetStatus === EntityStatusValue.Published ? 'republish' : 'update';

  // Sync TO other environment (current -> target) using chosen operation.
  // Errors are surfaced via options.onError, which useEntityUpdate forwards
  // to the underlying react-query mutation.
  const syncToOtherEnvironmentMutation = useEntityUpdate({
    entity: configEntity,
    instanceId,
    environment: otherEnvironmentKey,
    operation,
    data: alterDataForEnvironmentEntitySync({
      sourceData: currentEnvironmentData,
      targetData: otherEnvironmentData,
    }),
    options,
  });

  // Depends on `mutate` rather than the mutation result, which useMutation rebuilds as a
  // fresh object literal every render. Depending on the result made this callback change
  // identity every render, and it is carried into the entity details context value -- so the
  // whole form tree re-rendered on every render of the multi-environment container.
  const { mutate: syncToOtherEnvironment } = syncToOtherEnvironmentMutation;

  const handleEnvironmentSync = useCallback(() => {
    syncToOtherEnvironment();
  }, [syncToOtherEnvironment]);

  return { handleEnvironmentSync };
};
